/**
 * The dense table's keyboard model, as a function of (key, state, rows).
 *
 * `components.md`: *"`↑↓` move · `Space` select · `Shift+↑↓` extend · `Enter`
 * open · `/` focus search · `Esc` clear selection."*
 *
 * Six bindings, and every one of them is a decision about what happens to a
 * selection — which is why they are here rather than in a `onKeyDown` handler
 * eleven lines long inside the component. A handler can only be tested by
 * driving a browser; this can be tested by calling it, which is the difference
 * between a rule that is checked and a rule that is described.
 *
 * ## Moving is not selecting
 *
 * Arrow keys move the **active** row and change nothing else. That is the
 * behaviour of every list a user has already learned, and the alternative —
 * arrow-to-select — makes it impossible to look at a row without acting on it,
 * which on a screen whose bulk actions publish things is the wrong default.
 *
 * ## Ineligible rows are passed over, not skipped
 *
 * A tombstoned product is **visible** (`components.md`, and the reason is in its
 * primary cell), so the cursor lands on it and the user can read why. What it
 * cannot do is enter a selection: `Space` on it is swallowed, and a shift-extend
 * across it takes every eligible row in the range and leaves it behind. Skipping
 * it with the cursor instead would answer *"why can't I reach my product"* with
 * silence, which is the same failure as hiding the row.
 *
 * ## The cursor does not carry a selection, and that is a correctness rule
 *
 * It used to. {@link TableCursor} held a `selection` field, and `Space`, `Escape`
 * and `Shift+↑↓` each returned a cursor whose selection they had changed — while
 * `DataTable` rendered selection from its **`selection` prop** and never read the
 * cursor's copy. Two sources of truth, one of them rendered. **Three of the six
 * bindings above therefore did nothing at all**: the state moved, and no pixel
 * followed it.
 *
 * Every suite was green throughout. This module's own tests asserted the returned
 * value and were right about it; the defect lived entirely in the composition,
 * where neither `packages/ui` nor `apps/app` can look, because neither runs jsdom.
 * It is c37c's shape exactly, one field over.
 *
 * So the selection arrives as an **argument** and leaves as a **return value**,
 * and the cursor is position alone. A model that owns a copy of state its caller
 * also owns can always drift from it; one that is handed the value and returns
 * the next one cannot. The caller applies it to the same state the table renders,
 * because there is now only one.
 *
 * `selection` is optional, and its absence is a real arrangement rather than a
 * degenerate one: the audit log's table has no selection concept at all. There,
 * `Space` and `Escape` are **handled and change nothing** — swallowed for the same
 * reason `Space` on an ineligible row is, since an unhandled `Space` on a focused
 * grid scrolls the page — and `Shift+↑↓` degrades to a plain move.
 */
import { selectRows, toggleRow, clearSelection, } from "./selection.js";
export const INITIAL_CURSOR = {
    active: -1,
    anchor: -1,
};
const NOTHING = { handled: false, cursor: null };
/**
 * Every eligible id between two indices, inclusive, in row order.
 *
 * Order matters even though the result is a `Set`: the bulk bar reports a count
 * and the caller may iterate, and a range built backwards would publish rows in
 * an order the user did not see them in.
 */
function eligibleRange(rows, a, b) {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const ids = [];
    for (let i = lo; i <= hi; i += 1) {
        const row = rows[i];
        if (row !== undefined && row.ineligible === undefined)
            ids.push(row.id);
    }
    return ids;
}
/**
 * @param selection The caller's current selection, or `undefined` for a table
 *   that has no selection concept. See the header: absence is a real arrangement,
 *   and it makes the selection keys swallow rather than misfire.
 */
export function tableKeyAction(event, cursor, selection, rows) {
    if (rows.length === 0)
        return NOTHING;
    const clamp = (i) => Math.min(Math.max(i, 0), rows.length - 1);
    switch (event.key) {
        case "ArrowDown":
        case "ArrowUp": {
            const step = event.key === "ArrowDown" ? 1 : -1;
            // From "not entered" (-1), the first Down lands on row 0 and the first Up
            // lands on the last row — entering the list from the end you pressed
            // toward, rather than always at the top.
            const next = cursor.active < 0
                ? step === 1
                    ? 0
                    : rows.length - 1
                : clamp(cursor.active + step);
            if (!event.shiftKey) {
                return { handled: true, cursor: { active: next, anchor: next } };
            }
            // A table with no selection cannot extend one, so shift degrades to a plain
            // move rather than doing nothing — the cursor still has somewhere to go.
            if (selection === undefined) {
                return { handled: true, cursor: { active: next, anchor: next } };
            }
            // Shift with no anchor yet anchors where the extend begins, so the first
            // Shift+Down selects one row rather than nothing.
            const anchor = cursor.anchor < 0 ? next : cursor.anchor;
            return {
                handled: true,
                cursor: { active: next, anchor },
                // The range **replaces** the selection rather than adding to it. A
                // shift-extend that accumulated could never be shrunk by moving back
                // toward the anchor, which is the whole gesture.
                //
                // Replace also means a shift-extend leaves `filter` mode for `rows` mode,
                // which is correct and worth saying: the user has just named an explicit
                // range, and "everything matching the filter" is not what they named.
                selection: selectRows(eligibleRange(rows, anchor, next)),
            };
        }
        case " ": {
            const row = rows[cursor.active];
            if (row === undefined)
                return NOTHING;
            // Handled in every branch below — see {@link TableKeyResult.handled}.
            if (row.ineligible !== undefined)
                return { handled: true, cursor: null };
            if (selection === undefined)
                return { handled: true, cursor: null };
            // `toggleRow` is total over both modes, so this is already right for a
            // whole-filter selection: Space on a row inside one **excludes** it, and the
            // server's nine hundred becomes eight hundred and ninety-nine. That is what
            // unticking one row out of a select-all already means to the mouse.
            return {
                handled: true,
                cursor: null,
                selection: toggleRow(selection, row.id),
            };
        }
        case "Enter": {
            const row = rows[cursor.active];
            if (row === undefined)
                return NOTHING;
            // Opening a tombstoned product is allowed and should be: it is read-only
            // and terminal, and the screen that says so is the one the user is asking
            // for when they press Enter on it.
            return { handled: true, cursor: null, open: row.id };
        }
        case "/":
            return { handled: true, cursor: null, focusSearch: true };
        case "Escape":
            // Swallowed even with nothing to clear, and even with no selection at all.
            //
            // Named because it is a real edge and not an oversight: a future overlay
            // opened over a focused grid would find its own Escape consumed here. No
            // such overlay exists today — the account menu and the fold's drawer both
            // move focus out of the grid — so this is recorded for Phase 3's modals to
            // meet as a known edge rather than as a surprise.
            if (selection === undefined)
                return { handled: true, cursor: null };
            return { handled: true, cursor: null, selection: clearSelection() };
        default:
            return NOTHING;
    }
}
//# sourceMappingURL=keyboard.js.map